import type { ApiClient } from '../client';
import {
  LocationDeliveriesResponseSchema,
  type LocationDeliveriesQuery,
  type LocationDeliveriesResponse,
} from '../schemas/locationDeliveries';

// One building's delivery list, filtered. The location id rides the path — a number, so there is
// nothing in it to escape — and the filters ride the query string.
//
// `days` is ALWAYS on the wire: the read has no default window and answers 400 without one, so an
// omitted range would be a failed request rather than a 7-day list. `carrier` and `box` are only
// sent when they carry something — an omitted `carrier` is the wire form of «every carrier», and a
// blank «Buzón» is no filter at all rather than a filter nothing can match; both would read as a
// filter matching nothing if sent empty. When present, `carrier` is a carrier's INTEGER id, never
// the display name: the column behind it is an indexed FK, and two live carriers share «Amazon».
//
// An omitted `carrier` is a fresh server-side query over every carrier, not a filter over an
// already-materialized result (AS-042, CBX-11167) — which is what makes the widest default path
// the sizing case rather than a limit nobody has hit.
//
// `state` is the one param that repeats: the filter is multi-valued and each state gets its own
// `state=` on the wire, the DRF convention. An omitted param is «every state», and the whole
// filter is dropped rather than sent empty. Should the wire ever want them comma-joined instead,
// this loop is the only line that moves.
export async function fetchLocationDeliveries(
  client: ApiClient,
  locationId: number,
  query: LocationDeliveriesQuery,
): Promise<LocationDeliveriesResponse> {
  const params = new URLSearchParams({ days: String(query.days) });
  if (query.carrier !== undefined) params.set('carrier', String(query.carrier));
  const box = query.box?.trim() ?? '';
  if (box !== '') params.set('box', box);
  // `append`, never `set`: the state filter is multi-valued and travels as the param repeated once
  // per state, so `set` would overwrite its way down to the last one and quietly narrow the list.
  // The array is absent or non-empty by schema, so there is no empty spelling to guard against.
  for (const state of query.states ?? []) params.append('state', state);
  const data = await client.get<unknown>(
    `/app_service/panel/v1/locations/${String(locationId)}/deliveries?${params.toString()}`,
  );
  return LocationDeliveriesResponseSchema.parse(data);
}
